import React, { useState, useEffect } from 'react';
import { Share, Plus, X, RefreshCw } from 'lucide-react';

/* ── Environment checks ──────────────────────────────────────── */

function isIOS() {
  const ua = navigator.userAgent || '';
  // iPadOS 13+ reports itself as a Mac, so touch points are the giveaway.
  return /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** Only Safari can add to the home screen on iOS; Chrome and Firefox cannot. */
function isIOSSafari() {
  const ua = navigator.userAgent || '';
  return isIOS() && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

export function isStandalone() {
  return window.navigator.standalone === true
    || window.matchMedia('(display-mode: standalone)').matches;
}

const DISMISS_KEY = 'rdi_install_hint_dismissed';

function dismissed() {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch (_) { return false; }
}

/* ── Install hint ────────────────────────────────────────────── */

/**
 * iOS fires no beforeinstallprompt event and offers no install button, so the
 * only way onto the home screen is Share → Add to Home Screen. Nobody finds
 * that unprompted, so the steps are spelled out. On Android and desktop the
 * real prompt is used instead.
 */
export function InstallHint() {
  const [show, setShow] = useState(false);
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    if (isStandalone() || dismissed()) return undefined;

    if (isIOSSafari()) {
      // Held back a moment so it does not land on top of the login screen.
      const t = setTimeout(() => setShow(true), 2500);
      return () => clearTimeout(t);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  function close() {
    setShow(false);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* ignore */ }
  }

  async function install() {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (_) { /* dismissed */ }
    setDeferred(null);
    close();
  }

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
      <div className="mx-auto max-w-sm border bg-black/95 backdrop-blur p-4 shadow-2xl"
        style={{ borderColor: 'var(--gold)' }}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[9px] tracking-[0.25em] uppercase mb-1.5" style={{ color: 'var(--gold)' }}>
              Install
            </p>
            <p className="text-[12px] text-gray-200 leading-relaxed">
              Add RDI to your home screen — full screen, no address bar.
            </p>
          </div>
          <button onClick={close} aria-label="Dismiss"
            className="p-1 text-gray-600 hover:text-white shrink-0">
            <X size={15} />
          </button>
        </div>

        {deferred ? (
          <button onClick={install}
            className="mt-3 w-full text-[10px] tracking-[0.25em] uppercase font-semibold py-2.5"
            style={{ backgroundColor: 'var(--gold)', color: '#000' }}>
            Install App
          </button>
        ) : (
          <ol className="mt-3 space-y-2">
            <li className="flex items-center gap-2.5 text-[11px] text-gray-400">
              <span className="text-gray-600">1.</span>
              Tap <Share size={13} style={{ color: 'var(--gold)' }} /> in the Safari toolbar
            </li>
            <li className="flex items-center gap-2.5 text-[11px] text-gray-400">
              <span className="text-gray-600">2.</span>
              Choose <Plus size={13} style={{ color: 'var(--gold)' }} /> Add to Home Screen
            </li>
          </ol>
        )}
      </div>
    </div>
  );
}

/* ── Update bar ──────────────────────────────────────────────── */

/**
 * A home-screen app is rarely hard-refreshed, so without this a phone can sit
 * on an old build indefinitely. Shown only once a new worker is waiting.
 */
export function UpdateBar({ registration }) {
  if (!registration) return null;

  function update() {
    const waiting = registration.waiting;
    if (waiting) {
      waiting.postMessage('SKIP_WAITING');
      // The new worker takes over on activation; reloading picks up its assets.
      waiting.addEventListener('statechange', (e) => {
        if (e.target.state === 'activated') window.location.reload();
      });
      setTimeout(() => window.location.reload(), 1500);
    } else {
      window.location.reload();
    }
  }

  return (
    <div className="fixed inset-x-0 top-0 z-[60] px-4 pt-[calc(0.5rem+env(safe-area-inset-top))]">
      <button onClick={update}
        className="mx-auto flex max-w-sm w-full items-center justify-center gap-2 border bg-black/95
          backdrop-blur py-2.5 text-[10px] tracking-[0.2em] uppercase"
        style={{ borderColor: 'var(--gold)', color: 'var(--gold)' }}>
        <RefreshCw size={12} /> New version — tap to update
      </button>
    </div>
  );
}
